import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Check, Search, Loader2, ArrowLeft,
    ArrowRightLeft, Package, CheckCircle2, Calendar, X, AlertTriangle
} from 'lucide-react';
import API, { socket } from '../../services/api';
import Sidebar from '../../components/Sidebar';

const ReceptionProduction = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('a_recevoir');
    const [historique, setHistorique] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [inputs, setInputs] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    
    const [notification, setNotification] = useState({ 
        isOpen: false, 
        type: 'success', 
        message: '', 
        isConfirm: false, 
        onConfirm: null 
    });

    const fD = (d) => {
        if (!d) return { date: '--/--/----', time: '--:--:--' };
        const date = new Date(d);
        return {
            date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            time: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        };
    };

    const showToast = useCallback((message, type = 'success', isConfirm = false, onConfirm = null) => {
        setNotification({ isOpen: true, type, message, isConfirm, onConfirm });
        if (!isConfirm) {
            setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
        }
    }, []);

    const fetchHistorique = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/production-needs');
            const data = res.data || [];
            setHistorique(data);
            
            const initialInputs = {};
            data.forEach(item => {
                initialInputs[item.id] = item.quantite_recue ?? item.quantite_produite ?? '';
            });
            setInputs(initialInputs);
        } catch (err) { 
            showToast("Erreur de chargement", "error");
        } finally { 
            setLoading(false); 
        }
    }, [showToast]);

    useEffect(() => {
        fetchHistorique();
        if (!socket) return;
        const handleSync = () => setTimeout(() => fetchHistorique(), 600);
        socket.on('REFRESH_UI', handleSync);
        return () => socket.off('REFRESH_UI', handleSync);
    }, [fetchHistorique]);

    const handleInputChange = (id, value) => setInputs(prev => ({ ...prev, [id]: value }));

    const validerLigneDefinitive = async (item, force = false) => {
        const rawValue = inputs[item.id] !== undefined && inputs[item.id] !== '' 
            ? inputs[item.id] 
            : item.quantite_produite;
        const qte = parseFloat(rawValue);
        if (isNaN(qte) || qte <= 0) {
            return showToast("Quantité invalide", "error");
        }
        setIsProcessing(true);
        try {
            const res = await API.put(`/production-needs/${item.id}/receive`, { 
                qteRecue: qte,
                quantite_reelle_recue: qte,
                force_without_analytics: force 
            });
            
            if (res.data && res.data.needsConfirmation) {
                setIsProcessing(false);
                showToast(
                    "Aucun coût analytique trouvé pour cet article. Voulez-vous continuer sans analytique ?", 
                    'warning', 
                    true, 
                    () => validerLigneDefinitive(item, true)
                );
                return;
            }
            setNotification({ isOpen: false });
            showToast("Réception validée avec succès !");
            await fetchHistorique();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur de mise en stock", "error");
        } finally {
            setIsProcessing(false);
        }
    };

    const filterLogic = (list) => {
        return list.filter(item => {
            const matchesSearch = item.article_nom?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                   item.code_groupe?.toLowerCase().includes(searchTerm.toLowerCase());
            const itemDate = activeTab === 'recus' ? new Date(item.date_stockage) : new Date(item.date_emission);
            const matchesStart = startDate ? itemDate >= new Date(startDate) : true;
            const matchesEnd = endDate ? itemDate <= new Date(endDate + 'T23:59:59') : true;
            return matchesSearch && matchesStart && matchesEnd;
        });
    };

    const aRecevoirFiltered = filterLogic(historique.filter(h => h.statut_ligne === 'TERMINE'));
    const recusFiltered = filterLogic(historique.filter(h => h.statut_ligne === 'STOCKE'));
    const filtered = activeTab === 'a_recevoir' ? aRecevoirFiltered : recusFiltered;

    const s = {
        page: { display: 'flex', background: '#f1f5f9', height: '100vh', width: '100vw', overflow: 'hidden', fontFamily: 'Inter, sans-serif' },
        main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        header: { background: '#0f172a', padding: '15px 30px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        filterBar: { display: 'flex', gap: '15px', padding: '10px 30px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', alignItems: 'center' },
        statsBar: { display: 'flex', gap: '15px', padding: '15px 30px', background: '#fff', borderBottom: '1px solid #e2e8f0' },
        statCard: (active, color) => ({ 
            flex: 1, background: active ? `${color}08` : '#fff', padding: '12px 20px', borderRadius: '12px', 
            border: active ? `2px solid ${color}` : '1px solid #e2e8f0', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '15px', transition: '0.2s' 
        }),
        content: { flex: 1, padding: '20px 30px', overflowY: 'auto' },
        row: (isL) => ({ 
            background: '#fff', borderRadius: '10px', marginBottom: '8px', padding: '12px 20px', 
            display: 'grid', 
            gridTemplateColumns: isL ? '1.5fr 0.6fr 0.6fr 0.6fr 0.8fr 0.7fr 0.6fr' : '2fr 0.8fr 0.8fr 1.2fr 1fr', 
            alignItems: 'center', border: '1px solid #e2e8f0', borderLeft: isL ? '5px solid #10b981' : '5px solid #2563eb'
        }),
        dateCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '9px', color: '#64748b', borderLeft: '1px solid #f1f5f9' },
        dateInput: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', outline: 'none', background: '#fff' },
        toastContainer: { position: 'fixed', top: '25px', left: '50%', transform: 'translateX(-50%)', zIndex: 99999, width: '450px' },
        toast: (type) => ({ 
            background: 'white', padding: '15px 20px', borderRadius: '15px', 
            boxShadow: '0 15px 35px rgba(0,0,0,0.2)', border: `1px solid ${type === 'error' ? '#fee2e2' : type === 'warning' ? '#fef3c7' : '#dcfce7'}`,
            display: 'flex', flexDirection: 'column', gap: '10px' 
        })
    };

    return (
        <div style={s.page}>
            <Sidebar />
            
            {notification.isOpen && (
                <div style={s.toastContainer}>
                    <div style={s.toast(notification.type)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {notification.type === 'error' ? <X color="#ef4444" /> : notification.type === 'warning' ? <AlertTriangle color="#f59e0b" /> : <Check color="#10b981" />}
                            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px', flex: 1 }}>{notification.message}</span>
                            {!notification.isConfirm && <X size={18} style={{cursor:'pointer'}} onClick={() => setNotification({isOpen:false})} />}
                        </div>
                        
                        {notification.isConfirm && (
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '5px' }}>
                                <button onClick={() => setNotification({isOpen:false})} style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>ANNULER</button>
                                <button onClick={notification.onConfirm} style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>CONFIRMER QUAND MÊME</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div style={s.main}>
                <header style={s.header}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '8px', borderRadius: '50%', marginRight: '15px' }}><ArrowLeft size={22} /></button>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '900' }}>MAGASINAGE & RÉCEPTION (PAR LOTS & RENDEMENT)</h2>
                    </div>
                </header>

                <div style={s.filterBar}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input placeholder="Rechercher article ou lot..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ ...s.dateInput, width: '100%', paddingLeft: '35px' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Calendar size={16} color="#64748b" />
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={s.dateInput} />
                        <span style={{fontSize: '12px', fontWeight: 'bold'}}>À</span>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={s.dateInput} />
                    </div>
                </div>

                <div style={s.statsBar}>
                    <div onClick={() => setActiveTab('a_recevoir')} style={s.statCard(activeTab === 'a_recevoir', '#2563eb')}>
                        <Package size={24} color="#2563eb" />
                        <div><div style={{ fontSize: '10px', color: '#64748b', fontWeight: '800' }}>EN ATTENTE</div><div style={{ fontSize: '18px', fontWeight: '900' }}>{aRecevoirFiltered.length}</div></div>
                    </div>
                    <div onClick={() => setActiveTab('recus')} style={s.statCard(activeTab === 'recus', '#10b981')}>
                        <CheckCircle2 size={24} color="#10b981" />
                        <div><div style={{ fontSize: '10px', color: '#64748b', fontWeight: '800' }}>HISTORIQUE</div><div style={{ fontSize: '18px', fontWeight: '900' }}>{recusFiltered.length}</div></div>
                    </div>
                </div>

                <div style={s.content}>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: activeTab === 'recus' ? '1.5fr 0.6fr 0.6fr 0.6fr 0.8fr 0.7fr 0.6fr' : '2fr 0.8fr 0.8fr 1.2fr 1fr', 
                        padding: '0 20px 10px', fontSize: '9px', fontWeight: '900', color: '#64748b', textTransform: 'uppercase' 
                    }}>
                        <div>Article / Lot</div>
                        <div>Prévu (Demandé)</div>
                        <div>Produit (Réel)</div>
                        {activeTab === 'recus' ? (
                            <>
                                <div style={{textAlign:'center', color:'#10b981'}}>Stocké</div>
                                <div style={{textAlign:'center'}}>Date Stockage</div>
                                <div style={{textAlign:'center'}}>CMP Réel</div>
                                <div style={{textAlign:'right'}}>Statut</div>
                            </>
                        ) : (
                            <><div style={{ textAlign: 'center' }}>Saisie Réel / Lots</div><div style={{ textAlign: 'right' }}>Action</div></>
                        )}
                    </div>

                    {loading ? (
                        <div style={{padding: '50px', textAlign: 'center'}}><Loader2 className="animate-spin" color="#2563eb" size={40} /></div>
                    ) : filtered.map(item => {
                        const isL = item.statut_ligne === 'STOCKE';
                        const currentInput = inputs[item.id] !== undefined ? inputs[item.id] : item.quantite_produite;
                        const isConforme = parseFloat(currentInput) === parseFloat(item.quantite_produite);

                        return (
                            <div key={item.id} style={s.row(isL)}>
                                <div>
                                    <div style={{ fontWeight: '900', fontSize: '12px' }}>{item.article_nom}</div>
                                    <div style={{ fontSize: '9px', color: '#2563eb', fontWeight: '700' }}>{item.code_groupe}</div>
                                    {item.observation_production && (
                                        <div style={{ fontSize: '8px', color: '#64748b', fontStyle: 'italic' }}>{item.observation_production}</div>
                                    )}
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: '700' }}>{item.quantite} {item.unite}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontWeight: '900', fontSize: '13px', color: '#2563eb' }}>{item.quantite_produite} {item.unite}</span>
                                    {!isL && <ArrowRightLeft size={14} color="#2563eb" style={{cursor: 'pointer'}} onClick={() => handleInputChange(item.id, item.quantite_produite)}/>}
                                </div>

                                {isL ? (
                                    <>
                                        <div style={{textAlign:'center', fontWeight: '900', fontSize: '13px', color: '#10b981'}}>{item.quantite_recue} {item.unite}</div>
                                        <div style={s.dateCol}><span>{fD(item.date_stockage).date}</span><b>{fD(item.date_stockage).time}</b></div>
                                        <div style={{textAlign:'center', fontWeight:'800', fontSize:'11px', color:'#b45309'}}>
                                            {Number(item.cout_unitaire_reel ?? 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})}
                                        </div>
                                        <div style={{textAlign:'right'}}><span style={{fontSize:'8px', background:'#dcfce7', color:'#16a34a', padding:'2px 4px', borderRadius:'3px', fontWeight:'900'}}>STOCKE</span></div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <input 
                                                type="number" 
                                                step="any"
                                                value={currentInput} 
                                                onChange={(e) => handleInputChange(item.id, e.target.value)} 
                                                style={{ width: '90px', padding: '6px', borderRadius: '6px', border: isConforme ? '2px solid #22c55e' : '1px solid #cbd5e1', textAlign: 'center', fontWeight: '900', fontSize: '14px', outline: 'none' }} 
                                            />
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <button 
                                                onClick={() => validerLigneDefinitive(item)} 
                                                disabled={isProcessing}
                                                style={{ background: isConforme ? '#16a34a' : '#2563eb', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: '900', cursor: 'pointer', fontSize: '11px' }}
                                            >
                                                {isProcessing ? <Loader2 className="animate-spin" size={14} /> : "VALIDER STOCK"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ReceptionProduction;